import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

interface UndoAction {
  id: string
  type: 'complete' | 'delete' | 'update' | 'batch-complete' | 'batch-delete' | 'reorder'
  label: string
  undo: () => Promise<void>
  redo: () => Promise<void>
  timestamp: number
}

interface UndoState {
  undoStack: UndoAction[]
  redoStack: UndoAction[]
  currentToast: UndoAction | null
  toastTimeoutId: ReturnType<typeof setTimeout> | null
  isProcessing: boolean

  pushAction: (action: Omit<UndoAction, 'id' | 'timestamp'>) => void
  undo: () => Promise<void>
  redo: () => Promise<void>
  dismissToast: () => void
}

const MAX_STACK_SIZE = 20
const TOAST_DURATION = 5000

export const useUndoStore = create<UndoState>()(
  devtools(
    (set, get) => ({
      undoStack: [],
      redoStack: [],
      currentToast: null,
      toastTimeoutId: null,
      isProcessing: false,

      pushAction: (action) => {
        const fullAction: UndoAction = {
          ...action,
          id: crypto.randomUUID(),
          timestamp: Date.now(),
        }

        // 이전 토스트 타이머 정리
        const prevTimeout = get().toastTimeoutId
        if (prevTimeout) clearTimeout(prevTimeout)

        // 자동 사라짐 타이머
        const timeoutId = setTimeout(() => {
          set({ currentToast: null, toastTimeoutId: null })
        }, TOAST_DURATION)

        set((state) => ({
          undoStack: [...state.undoStack.slice(-(MAX_STACK_SIZE - 1)), fullAction],
          redoStack: [],
          currentToast: fullAction,
          toastTimeoutId: timeoutId,
        }))
      },

      undo: async () => {
        if (get().isProcessing) return
        const { undoStack } = get()
        if (undoStack.length === 0) return

        set({ isProcessing: true })
        try {
          const action = undoStack[undoStack.length - 1]
          await action.undo()

          // 이전 토스트 타이머 정리
          const prevTimeout = get().toastTimeoutId
          if (prevTimeout) clearTimeout(prevTimeout)

          set((state) => ({
            undoStack: state.undoStack.slice(0, -1),
            redoStack: [...state.redoStack.slice(-(MAX_STACK_SIZE - 1)), action],
            currentToast: null,
            toastTimeoutId: null,
            isProcessing: false,
          }))
        } catch (err) {
          set({ isProcessing: false })
          throw err
        }
      },

      redo: async () => {
        if (get().isProcessing) return
        const { redoStack } = get()
        if (redoStack.length === 0) return

        set({ isProcessing: true })
        try {
          const action = redoStack[redoStack.length - 1]
          await action.redo()

          const prevTimeout = get().toastTimeoutId
          if (prevTimeout) clearTimeout(prevTimeout)

          set((state) => ({
            redoStack: state.redoStack.slice(0, -1),
            undoStack: [...state.undoStack.slice(-(MAX_STACK_SIZE - 1)), action],
            currentToast: null,
            toastTimeoutId: null,
            isProcessing: false,
          }))
        } catch (err) {
          set({ isProcessing: false })
          throw err
        }
      },

      dismissToast: () => {
        const prevTimeout = get().toastTimeoutId
        if (prevTimeout) clearTimeout(prevTimeout)
        set({ currentToast: null, toastTimeoutId: null })
      },
    }),
    { name: 'undo-store' }
  )
)
